import React, { useState, useContext } from 'react';

import { useHistory } from 'react-router-dom';
import AddRoundedIcon from '@material-ui/icons/AddRounded';

import useLandingPageData from 'hooks/useLandingPageData';
import useOrganizationApi from 'hooks/useOrganizationApi';
import EmptyTableState from 'components/EmptyTableState';
import Paper from 'components/Paper';

import catchCancel from 'helpers/catchCancel';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';

import { Context } from 'components/Store';
import ActionsMenu from 'components/ActionsMenu';
import UiKitButton from 'components/UiKit/Button';
import DeleteModal from 'components/Modals/DeleteModal';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import EmptyLandingOverlay from 'components/EmptyLandingOverlay';

import { paths } from 'routes/paths';
import { PEOPLE_LANDING_MENU } from 'utilities/menus';
import { parseServerError } from 'helpers/errorHelpers';
import NoPeopleFoundIcon from 'assets/no_applications_found.svg';
import PeopleList from './components/PeopleList';
import PeopleSearchBar from './components/PeopleSearchBar';
import { hasAccess, ROLES } from '../../utilities/access';

const People = () => {
  const [peoplePageNumber, setPeoplePageNumber] = useState(0);

  const {
    isLoading: isPeopleLoading,
    items: people,
    setItems: setPeople,
    totalItems: totalPeople,
    setReload,
    setSearchText: setPeopleSearchText,
    setSort,
    setSortByOverride
  } = useLandingPageData(
    'worker/people',
    ['contactInfo.name.givenName', 'contactInfo.name.surname'],
    'contactInfo.name.givenName',
    peoplePageNumber
  );

  const history = useHistory();
  const peopleAPI = useOrganizationApi('people');
  const amplitude = useContext(AmplitudeContext);
  const [
    {
      loggedInUserOrgPermission: { role }
    },
    dispatch
  ] = useContext(Context);

  const [userId, setUserId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('');

  const isLoading = () => {
    return isPeopleLoading || isDeleting;
  };

  const hasCreatePermission = () => {
    return hasAccess({ role }, { minAllowedRole: ROLES.FULL_CONTROL });
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!userId) return;

    amplitude.sendEventToAmplitude(amplitude.events.epic.People.deletePerson);

    setIsDeleting(true);
    closeDeleteModal();

    await peopleAPI
      .delete(userId)
      .promise.then(() => {
        setPeople(newPeople => newPeople.filter(({ id }) => id !== userId));
      })
      .catch(catchCancel)
      .catch(parseServerError(dispatch));

    setUserId(null);
    setIsDeleting(false);
  };

  const handleAction = async (action, user) => {
    const actions = {
      view: () => {
        history.push(`people/${user.id}/view`);
      },
      edit: () => {
        amplitude.sendEventToAmplitude(amplitude.events.epic.People.editPerson);
        history.push(`people/${user.id}`);
      },
      delete: async () => {
        setUserId(user.id);
        openDeleteModal();
      },
      print: () => {}
    };
    actions[action]();
  };

  const getAddPersonBtn = () => {
    return hasCreatePermission() ? (
      <UiKitButton
        id="add-person-button"
        variant="primary"
        icon={<AddRoundedIcon />}
        onClick={() => {
          history.push(`${paths.people}/person`);
        }}
      >
        Add Person
      </UiKitButton>
    ) : null;
  };

  const getActionsMenu = user => {
    return (
      <ActionsMenu
        portal
        onAction={action => {
          handleAction(action, user);
        }}
        options={PEOPLE_LANDING_MENU}
      />
    );
  };

  const handlePageChange = page => {
    setPeoplePageNumber(page);
  };

  const getView = () => {
    if (isLoading() || people.length > 0) {
      return (
        <PeopleList
          people={people}
          isLoading={isLoading()}
          onAction={handleAction}
          getMenu={getActionsMenu}
          totalPeople={totalPeople}
          peoplePageNumber={peoplePageNumber}
          handlePageChange={handlePageChange}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
        />
      );
    }

    if (searchText.trim()) {
      return (
        <Paper>
          <EmptyTableState
            containerClassName="w-full flex flex-col justify-center items-center py-8"
            title="No Results Found"
            message="Try a different search."
            overlayIcon={NoPeopleFoundIcon}
          />
        </Paper>
      );
    }

    return (
      <EmptyLandingOverlay subtitle="We were unable to find any person under this organization.">
        {getAddPersonBtn()}
      </EmptyLandingOverlay>
    );
  };

  const handleOrgChange = () => {
    setPeoplePageNumber(0);
    setReload(true);
  };

  const handleSort = (field, direction) => {
    const sortFields = {
      name: 'name',
      email: 'email',
      license: 'applicatorLicenseNumber',
      expirationDate: 'applicatorLicenseExpirationDateEpoch'
    };
    if (!sortFields[field]) return;

    const nextDirection = direction || 'asc';
    setSortByOverride(direction ? sortFields[field] : 'name');
    setSort(nextDirection);
    setSortBy(direction ? field : '');
    setSortDir(direction || '');
    setPeoplePageNumber(0);
  };

  return (
    <div className="w-full h-full" data-testid="people">
      <Breadcrumb onOrganizationSelect={handleOrgChange} hideCropSeasonDropdown>
        <Breadcrumb.Item title="People" value="All People" isLast />
      </Breadcrumb>
      <PeopleSearchBar
        onChange={searchTerm => {
          setSearchText(searchTerm || '');
          setPeoplePageNumber(0);
          setPeopleSearchText(searchTerm);
        }}
      >
        {(people.length > 0 || searchText.trim() || isLoading()) &&
          getAddPersonBtn()}
      </PeopleSearchBar>
      {getView()}
      <DeleteModal
        open={showDeleteModal}
        itemType="Person"
        onCancel={closeDeleteModal}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default People;
